import { FileTextIcon, PaperclipIcon, Trash2Icon } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

import {
  EXTENSIONES_ACEPTADAS,
  motivoDeRechazoDelAntecedente,
  MAXIMO_BYTES_DEL_ANTECEDENTE,
} from "@/components/informes/ArchivosDelDocumento";
import { formatearPeso } from "@/components/informes/ZonaDeImagen";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import { separarPorValidez, subirEnSecuencia, textoDeFallos } from "@/lib/subirEnSecuencia";
import type { ClaveDeSeccionDelPortal, SeccionDelPortal } from "@/lib/portalPublico";
import { useEliminarArchivo, useSubirArchivo } from "@/hooks/usePortalPublico";

/**
 * Los tres espacios donde el cliente deja sus archivos.
 *
 * ══ POR QUÉ TRES ESPACIOS Y NO UNA TARJETA POR DOCUMENTO ════════════════════
 *
 * Antes se pedían trece categorías de documento, una por tarjeta. El cliente no
 * sabe cuál es cuál —no distingue un certificado de dominio de una copia de
 * inscripción— y terminaba subiendo cada papel donde le parecía o dejándolo sin
 * subir. Ahora elige entre tres cosas que sí reconoce (lo legal, las tasaciones
 * que ya tiene, y lo que describe el campo) y el EQUIPO clasifica cada archivo
 * después. Por eso la pantalla lo dice con todas las letras.
 *
 * ══ LA LISTA DE LO PEDIDO ES ORIENTACIÓN, NO UN CONTADOR ════════════════════
 *
 * "Te pedimos, entre otros: …" nombra los documentos del presupuesto para que
 * el cliente sepa qué buscar, pero ya no hay casillas "Obligatorio" ni
 * "Recibido" por documento: sin saber qué archivo cubre qué documento, marcar
 * uno como recibido sería adivinar.
 *
 * ══ NO HAY DESCARGA DE LO QUE SUBIÓ ═════════════════════════════════════════
 *
 * Se ve el nombre y el peso, no un enlace. Volver a bajar el archivo que uno
 * mismo acaba de subir no resuelve nada, y cada endpoint público que sirve
 * bytes es superficie nueva. Lo que sí puede es quitarlo y volver a subirlo.
 */

/** Qué subir en cada espacio, dicho para quien no conoce los términos. */
const EXPLICACION: Record<ClaveDeSeccionDelPortal, string> = {
  legal:
    "Escrituras, certificados y documentos que acreditan quién es el dueño y qué se está tasando. Si tienes dudas, súbelo igual: nosotros lo ordenamos.",
  tasaciones:
    "Informes de tasación anteriores de esta propiedad, hechos por Consultek o por otra empresa.",
  descriptiva:
    "Todo lo que describa el campo: listados de maquinaria y equipos, catastro de plantaciones, inventarios, planos o fotos.",
};

export function SeccionDocumentos({
  token,
  secciones,
  documentosPedidos,
}: {
  token: string;
  secciones: SeccionDelPortal[];
  documentosPedidos: string[];
}) {
  const [activa, setActiva] = useState<ClaveDeSeccionDelPortal>("legal");

  return (
    <div className="space-y-4">
      <p className="text-ink-body text-sm text-pretty">
        No necesitas saber qué documento es cada uno: sube los archivos donde mejor
        correspondan y nuestro equipo los clasifica.
      </p>

      <Tabs value={activa} onValueChange={(valor) => setActiva(valor as ClaveDeSeccionDelPortal)}>
        <TabsList className="h-auto w-full flex-wrap justify-start">
          {secciones.map((seccion) => (
            <TabsTrigger key={seccion.clave} value={seccion.clave} className="h-auto py-1.5">
              {seccion.nombre}
              {seccion.archivos.length > 0 ? (
                <span className="text-ink-hint ml-1.5 font-mono text-xs">
                  {seccion.archivos.length}
                </span>
              ) : null}
            </TabsTrigger>
          ))}
        </TabsList>

        {secciones.map((seccion) => (
          <TabsContent key={seccion.clave} value={seccion.clave} className="mt-2">
            <TarjetaDeSeccion
              token={token}
              seccion={seccion}
              documentosPedidos={seccion.clave === "legal" ? documentosPedidos : []}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function TarjetaDeSeccion({
  token,
  seccion,
  documentosPedidos,
}: {
  token: string;
  seccion: SeccionDelPortal;
  documentosPedidos: string[];
}) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [enLote, setEnLote] = useState(false);

  const subir = useSubirArchivo(token);
  const eliminar = useEliminarArchivo(token);

  // Pausada NO es en vuelo: sin red, TanStack deja la mutación `isPending` sin
  // haber enviado nada y el botón giraría para siempre. Mismo criterio que en
  // `AceptarCotizacion`.
  const ocupado =
    enLote ||
    (subir.isPending && !subir.isPaused) || (eliminar.isPending && !eliminar.isPaused);

  const recibido = seccion.archivos.length > 0;

  async function adjuntar(archivos: File[]) {
    // El navegador rechaza lo que puede sin gastar la subida. NO es la
    // autoridad —la validación real mira los BYTES con finfo del lado del
    // servidor— pero evita mandar 32 MB por la red para descubrir que no
    // entran. Un archivo rechazado NO frena a los demás.
    const { validos, rechazados } = separarPorValidez(archivos, motivoDeRechazoDelAntecedente);

    setError(rechazados.length > 0 ? textoDeFallos(rechazados) : null);

    if (validos.length === 0) return;

    // Ocupada la tarjeta por TODO el lote: `subir.isPending` se apaga entre un
    // archivo y el siguiente, y ahí el botón se rehabilitaría a mitad de subida.
    setEnLote(true);

    try {
      const { subidos, fallos } = await subirEnSecuencia(
        validos,
        (archivo) => subir.mutateAsync({ seccion: seccion.clave, archivo }),
        // El 422 del duplicado llega como error de validación sobre el campo
        // `archivo`, y ese mensaje es el útil ("ese archivo ya lo recibimos").
        // El genérico solo aparece cuando no hay uno mejor.
        (fallo) =>
          parseValidationErrors(fallo)?.archivo ??
          getApiErrorMessage(fallo, "No pudimos subir el archivo. Inténtalo de nuevo."),
      );

      if (subidos.length > 0) {
        toast.success(
          subidos.length === 1 ? `Recibimos ${subidos[0].name}` : `Recibimos ${subidos.length} archivos`,
        );
      }

      const todos = textoDeFallos([...rechazados, ...fallos]);
      setError(todos || null);
    } finally {
      setEnLote(false);
    }
  }

  return (
    <section className="rounded-xl border p-5">
      <h3 className="sr-only">{seccion.nombre}</h3>

      <p className="text-ink-muted text-sm text-pretty">{EXPLICACION[seccion.clave]}</p>

      {/* La frase literal del presupuesto, solo en el espacio legal: es lo que
          el cliente va a buscar en su carpeta. */}
      {documentosPedidos.length > 0 ? (
        <p className="text-ink-body mt-2 text-sm text-pretty">
          Te pedimos, entre otros: {documentosPedidos.join(", ")}.
        </p>
      ) : null}

      {recibido ? (
        <ul className="mt-3 space-y-1.5">
          {seccion.archivos.map((archivo) => (
            <li
              key={archivo.id}
              className="bg-muted/40 flex items-center gap-2 rounded-md px-3 py-2"
            >
              <FileTextIcon className="text-ink-hint size-4 shrink-0" aria-hidden="true" />

              <span className="text-ink truncate text-sm font-semibold" title={archivo.nombre}>
                {archivo.nombre}
              </span>

              <span className="text-ink-hint ml-auto shrink-0 font-mono text-xs">
                {formatearPeso(archivo.bytes)}
              </span>

              {/* Solo lo suyo. Lo que cargó el equipo se ve pero no se quita:
                  el servidor lo decide y lo vuelve a verificar, así que acá no
                  se deduce nada — se obedece `puede_eliminarse`. */}
              {archivo.puede_eliminarse ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={ocupado}
                  aria-label={`Quitar ${archivo.nombre}`}
                  onClick={() =>
                    eliminar.mutate(archivo.id, {
                      onError: (fallo) =>
                        toast.error(
                          getApiErrorMessage(fallo, "No pudimos quitar el archivo."),
                        ),
                    })
                  }
                >
                  <Trash2Icon aria-hidden="true" />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {/* El input va oculto y lo dispara el botón: el control nativo no se
          puede estilar. `sr-only` y no `display:none` para que siga existiendo
          para la tecnología asistiva. */}
      <input
        ref={input}
        type="file"
        multiple
        accept={EXTENSIONES_ACEPTADAS}
        className="sr-only"
        aria-label={`Adjuntar archivos a ${seccion.nombre}`}
        onChange={(evento) => {
          // Se copia ANTES de limpiar: `files` es una lista viva.
          const archivos = Array.from(evento.target.files ?? []);
          // Se limpia SIEMPRE, incluso cuando los archivos se aceptan: sin esto,
          // volver a elegir el mismo archivo no dispara `change` —el valor no
          // cambió— y reintentar tras un error del servidor sería imposible sin
          // elegir otro archivo primero.
          evento.target.value = "";
          if (archivos.length > 0) void adjuntar(archivos);
        }}
      />

      <Button
        type="button"
        variant={recibido ? "ghost" : "soft"}
        size="sm"
        className="mt-3"
        disabled={ocupado}
        aria-label={`Adjuntar archivos a ${seccion.nombre}`}
        onClick={() => input.current?.click()}
      >
        {ocupado ? <Spinner /> : <PaperclipIcon aria-hidden="true" />}
        {recibido ? "Adjuntar más archivos" : "Adjuntar archivos"}
      </Button>

      {/* Una línea por archivo que no entró, "nombre: motivo". */}
      {error ? (
        <div className="mt-2">
          {error.split("\n").map((linea, i) => (
            <p key={`${i}-${linea}`} className="text-destructive text-sm">
              {linea}
            </p>
          ))}
        </div>
      ) : null}
    </section>
  );
}

/** Lo que el pie de la sección le dice al cliente sobre el tope de tamaño. */
export const LIMITE_EN_PALABRAS = `${MAXIMO_BYTES_DEL_ANTECEDENTE / (1024 * 1024)} MB`;
